import React, { useEffect, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { api } from '../../utils/api';
import ImageUploader from '../../components/ImageUploader';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  PageHeader, Panel, FormGroup, FormGrid, SaveBtn, SecondaryBtn,
  Toast, Row, ItemPill, Table,
} from './styles/AdminStyles';

const EditContact = () => {
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [phoneInput, setPhoneInput] = useState('');
  const [socialLabel, setSocialLabel] = useState('');
  const [socialUrl, setSocialUrl] = useState('');

  useEffect(() => {
    Promise.all([api.get('/contact'), api.get('/contact/messages').catch(() => [])])
      .then(([c, m]) => {
        setData({
          ...c,
          phones: Array.isArray(c.phones) ? c.phones : [],
          socialLinks: Array.isArray(c.socialLinks) ? c.socialLinks : [],
        });
        setMessages(Array.isArray(m) ? m : []);
        setLoading(false);
      });
  }, []);

  if (loading || !data) return <LoadingSpinner fullPage />;

  const setField = (f) => (e) => setData({ ...data, [f]: e.target.value });

  const addPhone = () => {
    if (!phoneInput.trim()) return;
    setData({ ...data, phones: [...data.phones, phoneInput.trim()] });
    setPhoneInput('');
  };
  const removePhone = (i) => setData({ ...data, phones: data.phones.filter((_, idx) => idx !== i) });

  const addSocial = () => {
    if (!socialLabel.trim() || !socialUrl.trim()) return;
    setData({ ...data, socialLinks: [...data.socialLinks, { platform: socialLabel.trim(), url: socialUrl.trim() }] });
    setSocialLabel(''); setSocialUrl('');
  };
  const removeSocial = (i) => setData({ ...data, socialLinks: data.socialLinks.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setToast(null);
    try {
      const saved = await api.put('/contact', {
        title: data.title || '',
        description: data.description || '',
        bannerImage: data.bannerImage || '',
        address: data.address || '',
        email: data.email || '',
        phones: data.phones,
        socialLinks: data.socialLinks,
      });
      setData({
        ...saved,
        phones: Array.isArray(saved.phones) ? saved.phones : [],
        socialLinks: Array.isArray(saved.socialLinks) ? saved.socialLinks : [],
      });
      api.clearCache();
      setToast({ type: 'success', message: 'Contact page saved.' });
    } catch (err) {
      setToast({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader>
        <div className="eyebrow">Content</div>
        <h1>Edit Contact Page</h1>
        <p>Manage the contact info shown on the website and review messages received from the contact form.</p>
      </PageHeader>

      <form onSubmit={save}>
        <Panel>
          <h2>Page Header</h2>
          <ImageUploader
            label="Banner Image"
            value={data.bannerImage}
            onChange={val => setData({ ...data, bannerImage: val })}
            onRemove={() => setData({ ...data, bannerImage: '' })}
          />
          <FormGroup>
            <label>Title</label>
            <input value={data.title || ''} onChange={setField('title')} />
          </FormGroup>
          <FormGroup>
            <label>Description</label>
            <textarea value={data.description || ''} onChange={setField('description')} />
          </FormGroup>
        </Panel>

        <Panel>
          <h2>Contact Details</h2>
          <FormGrid $cols={2}>
            <FormGroup>
              <label>Address</label>
              <input value={data.address || ''} onChange={setField('address')} />
            </FormGroup>
            <FormGroup>
              <label>Email</label>
              <input type="email" value={data.email || ''} onChange={setField('email')} />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <label>Phone Numbers</label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                value={phoneInput}
                onChange={e => setPhoneInput(e.target.value)}
                placeholder="Add a phone number"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhone(); } }}
                style={{ padding: '0.75rem 0.9rem', border: '1px solid #E9E9E6', flex: 1 }}
              />
              <SecondaryBtn type="button" onClick={addPhone}><FaPlus /></SecondaryBtn>
            </div>
            <div>
              {data.phones.map((p, i) => (
                <ItemPill key={i}>
                  {p}
                  <button type="button" onClick={() => removePhone(i)}><FaTimes /></button>
                </ItemPill>
              ))}
            </div>
          </FormGroup>

          <FormGroup>
            <label>Social Links</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, marginBottom: 8 }}>
              <input
                value={socialLabel}
                onChange={e => setSocialLabel(e.target.value)}
                placeholder="Platform (e.g. LinkedIn)"
                style={{ padding: '0.75rem 0.9rem', border: '1px solid #E9E9E6' }}
              />
              <input
                value={socialUrl}
                onChange={e => setSocialUrl(e.target.value)}
                placeholder="URL"
                style={{ padding: '0.75rem 0.9rem', border: '1px solid #E9E9E6' }}
              />
              <SecondaryBtn type="button" onClick={addSocial}><FaPlus /> Add</SecondaryBtn>
            </div>
            <div>
              {data.socialLinks.map((s, i) => (
                <ItemPill key={i}>
                  {s.platform}: {s.url}
                  <button type="button" onClick={() => removeSocial(i)}><FaTimes /></button>
                </ItemPill>
              ))}
            </div>
          </FormGroup>
        </Panel>

        <Row>
          <SaveBtn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</SaveBtn>
          {toast && <Toast $error={toast.type === 'error'}>{toast.message}</Toast>}
        </Row>
      </form>

      <Panel>
        <h2>Messages</h2>
        <div className="subtitle">Messages submitted through the public contact form.</div>
        <Table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Email</th>
              <th>Subject</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '1.5rem' }}>No messages yet.</td></tr>
            )}
            {messages.map(m => (
              <tr key={m.id}>
                <td>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</td>
                <td>{m.name}</td>
                <td>{m.email}</td>
                <td>{m.subject}</td>
                <td style={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>{m.message}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Panel>
    </>
  );
};

export default EditContact;
